import {
  Radio as ReactAriaRadio,
  type RadioProps as ReactAriaRadioProps,
  composeRenderProps,
} from "react-aria-components";

import { useUIVersion } from "#hooks";
import { cn, customTv } from "#utils";

const radioStyles = customTv({
  // `relative` is required so the visually-hidden <input> rendered by
  // react-aria-components (which uses `position: absolute`) is positioned
  // relative to this label rather than the document root. Without it,
  // clicking the radio can scroll the page out of view when the
  // radio is nested inside a scrollable container (e.g. a table).
  base: "relative flex items-center gap-2 text-label-medium transition",
  variants: {
    isDisabled: {
      true: "text-text-disabled cursor-not-allowed",
    },
    uiVersion: {
      v1: "",
      v2: "",
    },
  },
  defaultVariants: {
    uiVersion: "v2",
  },
});

const circleStyles = customTv({
  base: "shrink-0 flex items-center justify-center transition",
  variants: {
    uiVersion: {
      v1: "w-5 h-5 rounded-full border-2 border-legacy-navy-L30 bg-neutral-white",
      v2: "w-4 h-4 rounded-4 bg-neutral-white border border-outline-input-enabled",
    },
    isSelected: {
      true: "",
      false: "",
    },
    isDisabled: {
      true: "",
      false: "",
    },
    isFocusVisible: {
      true: "",
      false: "",
    },
    isHovered: {
      true: "",
      false: "",
    },
  },
  compoundVariants: [
    // V2 styling
    {
      uiVersion: "v2",
      isSelected: true,
      class: "!border-[5px] border-outline-brand",
    },
    {
      uiVersion: "v2",
      isDisabled: true,
      class: "border-outline-input-disabled",
    },
    {
      uiVersion: "v2",
      isFocusVisible: true,
      class: "border-2 border-outline-brand",
    },
    {
      uiVersion: "v2",
      isHovered: true,
      class: "border-outline-input-hover",
    },
    {
      uiVersion: "v2",
      isSelected: true,
      isHovered: true,
      class: "border-interactive-primary-button-hover-background",
    },
    {
      uiVersion: "v2",
      isSelected: true,
      isFocusVisible: true,
      class: "border-interactive-primary-button-focus-background",
    },
    {
      uiVersion: "v2",
      isDisabled: true,
      isHovered: true,
      class: "border-outline-input-disabled",
    },
    {
      uiVersion: "v2",
      isSelected: true,
      isDisabled: true,
      class:
        "border-interactive-secondary-button-focus-background bg-interactive-secondary-button-hover-background",
    },
    {
      uiVersion: "v2",
      isSelected: true,
      isDisabled: true,
      isHovered: true,
      class: "border-interactive-secondary-button-focus-background",
    },
    // V1 MUI-like styling
    {
      uiVersion: "v1",
      isSelected: false,
      isHovered: true,
      class: "border-legacy-navy-L30",
    },
    {
      uiVersion: "v1",
      isSelected: true,
      class: "border-legacy-orange-base border-2",
    },
    {
      uiVersion: "v1",
      isSelected: true,
      isHovered: true,
      class: "border-legacy-orange-D10",
    },
    {
      uiVersion: "v1",
      isFocusVisible: true,
      class: "outline-2 outline-legacy-orange-base outline-offset-1",
    },
    {
      uiVersion: "v1",
      isDisabled: true,
      class: "border-legacy-neutrals-denseFog opacity-50",
    },
  ],
  defaultVariants: {
    uiVersion: "v2",
  },
});

export interface RadioProps extends ReactAriaRadioProps {
  className?: string;
}

export function Radio({ className, ...props }: RadioProps) {
  const uiVersion = useUIVersion();

  return (
    <ReactAriaRadio
      className={composeRenderProps(className, (className, renderProps) =>
        radioStyles({ ...renderProps, className, uiVersion }),
      )}
      {...props}
    >
      {(renderProps) => (
        <>
          <div
            className={circleStyles({
              isSelected: renderProps.isSelected,
              isDisabled: renderProps.isDisabled,
              isFocusVisible: renderProps.isFocusVisible,
              isHovered: renderProps.isHovered,
              uiVersion,
            })}
          >
            {uiVersion === "v1" && renderProps.isSelected ? (
              <span
                className={cn(
                  "h-2-5 w-2-5 rounded-full bg-legacy-orange-base",
                  renderProps.isDisabled ? "bg-legacy-neutrals-denseFog" : "",
                )}
              />
            ) : null}
          </div>
          {props.children}
        </>
      )}
    </ReactAriaRadio>
  );
}
